/**
 * Connect-time protocol version negotiation (opt-in via
 * `ClientOptions.versionNegotiation`): the option surface, the probe window (a
 * raw transport exchange run before the Protocol machinery attaches), and the
 * negotiation engine driving the pure {@linkcode classifyProbeOutcome} classifier.
 *
 * Invariants: the probe uses string ids and consumes no Protocol message ids, so
 * a legacy fallback's `initialize` is byte-equivalent to a plain legacy connect;
 * the transport's protocol-version slot is never mutated during negotiation
 * (probe headers derive from the probe message body) and is set exactly once
 * after a modern resolution; while the probe window is open, inbound messages
 * that are not the probe response are dropped with zero bytes written back.
 */
import type { ClientCapabilities, DiscoverResult, Implementation, JSONRPCRequest, Transport } from '@modelcontextprotocol/core-internal';
import {
    codecForVersion,
    isJSONRPCErrorResponse,
    isJSONRPCResultResponse,
    isModernProtocolVersion,
    legacyProtocolVersions,
    modernProtocolVersions,
    SdkError,
    SdkErrorCode,
    SdkHttpError,
    SUPPORTED_MODERN_PROTOCOL_VERSIONS
} from '@modelcontextprotocol/core-internal';

import { UnauthorizedError } from './auth';
import { isAuthSeamEscape } from './authSeam';
import type { ProbeEnvironment, ProbeOutcome, ProbeTransportKind, ProbeVerdict } from './probeClassifier';
import { classifyProbeOutcome } from './probeClassifier';

/**
 * Probe policy for `'auto'` and pinned negotiation modes.
 *
 * There is no special probe timeout opinion: the probe inherits the client's
 * STANDARD request timeout unless `timeoutMs` overrides it.
 */
export interface VersionNegotiationProbeOptions {
    /**
     * Timeout for the probe exchange, in milliseconds.
     *
     * The timeout verdict is transport-aware: on stdio, a probe that gets no
     * response within the timeout indicates a legacy server and falls back to
     * the `initialize` handshake (measured on the disposable sibling for the
     * SDK's own stdio transport, with the fallback running on the session
     * child's fresh pipe; in place for custom stdio-shaped transports); on
     * HTTP, where a deployed server answers and silence means an outage,
     * `connect()` rejects with the standard typed timeout error instead.
     *
     * @default the standard request timeout (`DEFAULT_REQUEST_TIMEOUT_MSEC`, or the `timeout` passed to `connect()`)
     */
    timeoutMs?: number;

    /**
     * Number of times to re-send the probe after a timeout before reaching the
     * timeout verdict. Governs timeout re-sends only — the spec-mandated
     * `-32022` corrective continuation (select-and-continue with a mutual
     * version) is a separate negotiation step and is never counted against
     * `maxRetries`.
     *
     * @default 0 (no retries)
     */
    maxRetries?: number;
}

/**
 * Negotiation mode:
 *
 * - `'legacy'` — no negotiation: the plain 2025 connect sequence, byte-identical
 *   to a client without this option.
 * - `'auto'` — probe with `server/discover` at connect; conservative fallback to
 *   the plain legacy `initialize` handshake unless the outcome is definitive
 *   modern evidence. Network outage rejects with a typed connect error; a
 *   probe timeout falls back to `initialize` on stdio (a silent server on a
 *   local pipe is a legacy server) and rejects with a typed timeout error on
 *   HTTP (silence there is an outage). On the SDK's stdio transport (the base
 *   `StdioClientTransport` exactly; subclasses probe in place) the probe
 *   runs on a short-lived sibling process spawned from the same parameters
 *   (its stderr is discarded) and the caller's transport starts once, after
 *   the era is known — so a child that exits on the unrecognized probe, the
 *   shape of servers built on SDKs that terminate on any pre-`initialize`
 *   request, is simply a legacy server. A mid-probe connection close on HTTP
 *   (or on a custom stdio-shaped transport, which probes in place) rejects
 *   with the typed connect error.
 * - `{ pin: '<version>' }` — modern era at exactly the pinned revision: the
 *   connect-time `server/discover` must offer it. No fallback — anything else
 *   fails loudly with a typed error.
 */
export type VersionNegotiationMode = 'legacy' | 'auto' | { pin: string };

/**
 * Opt-in protocol version negotiation, configured on
 * `ClientOptions.versionNegotiation`.
 */
export interface VersionNegotiationOptions {
    /**
     * @default 'legacy'
     */
    mode?: VersionNegotiationMode;

    /**
     * Probe timeout/retry policy (only consulted by the probing modes).
     */
    probe?: VersionNegotiationProbeOptions;
}

/**
 * The default mode when `versionNegotiation` (or its `mode`) is absent;
 * changing the default later is a flip of this single line.
 */
const DEFAULT_VERSION_NEGOTIATION_MODE: VersionNegotiationMode = 'legacy';

/** A fully resolved negotiation plan for one `connect()` call. */
export type ResolvedVersionNegotiation =
    | { kind: 'legacy' }
    | {
          kind: 'auto';
          /** Modern versions this client offers, in preference order (never empty). */
          modernVersions: string[];
          /** Whether this client can fall back to the legacy `initialize` handshake. */
          fallbackAvailable: boolean;
          probe: VersionNegotiationProbeOptions;
      }
    | { kind: 'pin'; version: string; probe: VersionNegotiationProbeOptions };

/**
 * Resolve the negotiation options into a per-connect plan. The raw (not
 * defaulted) `supportedProtocolVersions` option supplies the modern offer list;
 * a list without any legacy version makes this a modern-only client (no fallback).
 */
export function resolveVersionNegotiation(
    options: VersionNegotiationOptions | undefined,
    supportedProtocolVersionsOption: readonly string[] | undefined
): ResolvedVersionNegotiation {
    const mode = options?.mode ?? DEFAULT_VERSION_NEGOTIATION_MODE;
    if (mode === 'legacy') {
        return { kind: 'legacy' };
    }
    const probe = options?.probe ?? {};
    if (typeof mode === 'object') {
        if (!isModernProtocolVersion(mode.pin)) {
            throw new TypeError(
                `versionNegotiation: { pin: '${mode.pin}' } is not a modern protocol revision — ` +
                    `pinning is for 2026-07-28 and later; omit versionNegotiation (or use mode: 'legacy') for 2025-era servers.`
            );
        }
        return { kind: 'pin', version: mode.pin, probe };
    }
    const explicitModern = supportedProtocolVersionsOption ? modernProtocolVersions(supportedProtocolVersionsOption) : [];
    const modernVersions = explicitModern.length > 0 ? explicitModern : [...SUPPORTED_MODERN_PROTOCOL_VERSIONS];
    const fallbackAvailable = supportedProtocolVersionsOption ? legacyProtocolVersions(supportedProtocolVersionsOption).length > 0 : true;
    return { kind: 'auto', modernVersions, fallbackAvailable, probe };
}

/** Detect the probe environment for the network-failure row — see {@linkcode ProbeEnvironment}. */
export function detectProbeEnvironment(): ProbeEnvironment {
    const g = globalThis as { window?: unknown; document?: unknown };
    return g.window !== undefined && g.document !== undefined ? 'browser' : 'node';
}

/**
 * Detect the transport class for the transport-aware timeout and closed verdicts (see
 * {@linkcode ProbeTransportKind}). The stdio child-process transport is
 * recognized structurally (`stderr`/`pid` accessors, no `instanceof` — safe
 * across bundles); everything else is treated like HTTP.
 */
export function detectProbeTransportKind(transport: Transport): ProbeTransportKind {
    return 'stderr' in transport && 'pid' in transport ? 'stdio' : 'http';
}

/** Raw reply from one probe exchange, before normalization. */
type RawProbeReply =
    | { kind: 'response'; result?: unknown; error?: { code: number; message: string; data?: unknown } }
    | { kind: 'send-error'; error: unknown }
    | { kind: 'closed' }
    | { kind: 'timeout' };

/**
 * Temporary ownership of a raw transport for the negotiation exchange, before
 * the Protocol machinery attaches. `open()` installs the window's handlers and
 * starts the transport; `release()` detaches them and arms a one-shot `start()`
 * pass-through so the subsequent Protocol connect (which always starts its
 * transport) takes over the already-started channel without a double-start error.
 *
 * Handlers a caller pre-set on the transport before `connect()` are saved on
 * open and restored on detach, so `Protocol.connect()` finds and chains them
 * exactly as it would on a plain (non-negotiating) connect. Error and close
 * events are forwarded to the saved handlers during the window, so negotiation
 * does not change what a pre-attached observer sees of the transport
 * lifecycle. (Inbound messages are deliberately NOT forwarded — the window's
 * drop-guard is a module invariant, and the probe reply has no plain-connect
 * counterpart; a pre-set `onmessage` is restored at detach and chained by
 * `Protocol.connect()` like the others.)
 */
class ProbeWindow {
    private _pending: { id: string; resolve: (reply: RawProbeReply) => void } | undefined;
    private _probeCounter = 0;
    private readonly _savedOnMessage: Transport['onmessage'];
    private readonly _savedOnError: Transport['onerror'];
    private readonly _savedOnClose: Transport['onclose'];
    private _closeDelivered = false;

    private constructor(private readonly _transport: Transport) {
        this._savedOnMessage = _transport.onmessage;
        this._savedOnError = _transport.onerror;
        this._savedOnClose = _transport.onclose;
    }

    static async open(transport: Transport): Promise<ProbeWindow> {
        const window = new ProbeWindow(transport);
        transport.onmessage = message => {
            const pending = window._pending;
            if (
                pending !== undefined &&
                (isJSONRPCResultResponse(message) || isJSONRPCErrorResponse(message)) &&
                message.id === pending.id
            ) {
                window._pending = undefined;
                if (isJSONRPCResultResponse(message)) {
                    pending.resolve({ kind: 'response', result: message.result });
                } else {
                    pending.resolve({ kind: 'response', error: message.error });
                }
                return;
            }
            // Probe-window guard: drop everything else with zero bytes written back (see module doc).
        };
        transport.onerror = error => {
            // Out-of-band transport errors are not necessarily fatal; the probe
            // resolves via a send failure, the close signal, or the timeout.
            window._savedOnError?.(error);
        };
        transport.onclose = () => {
            const pending = window._pending;
            if (pending !== undefined) {
                window._pending = undefined;
                pending.resolve({ kind: 'closed' });
            }
            // Forward exactly once: after a mid-window close is delivered, the
            // restored handler must not re-deliver it when cleanup paths call
            // `transport.close()` again.
            window._closeDelivered = true;
            window._savedOnClose?.();
        };
        try {
            await transport.start();
        } catch (error) {
            window.detach();
            throw error;
        }
        return window;
    }

    /**
     * Send one probe request and await its reply. Probe ids are strings, so they
     * never collide with Protocol's numeric ids (e.g. on a shared stdio pipe).
     */
    async exchange(buildRequest: (id: string) => JSONRPCRequest, timeoutMs: number): Promise<RawProbeReply> {
        const id = `server-discover-probe-${++this._probeCounter}`;
        return new Promise<RawProbeReply>(resolve => {
            let settled = false;
            const settle = (reply: RawProbeReply) => {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                if (this._pending?.id === id) {
                    this._pending = undefined;
                }
                resolve(reply);
            };
            const timer = setTimeout(() => settle({ kind: 'timeout' }), timeoutMs);
            this._pending = { id, resolve: settle };
            this._transport.send(buildRequest(id)).catch((error: unknown) => settle({ kind: 'send-error', error }));
        });
    }

    /** Detach the window's handlers, restoring any the caller pre-set, leaving the transport's own `start` untouched. */
    detach(): void {
        this._pending = undefined;
        this._transport.onmessage = this._savedOnMessage;
        this._transport.onerror = this._savedOnError;
        if (this._closeDelivered && this._savedOnClose !== undefined) {
            // The forwarded mid-window close is spent — the caller's cleanup
            // close() must not re-deliver it — but the slot must not be emptied
            // either: the caller still owns the transport, and its observer must
            // keep seeing later closes. The skip is scoped to the cleanup close
            // via disarmSpentCloseGuard(): transports whose close() re-fires
            // onclose (HTTP) consume it there; transports that never re-fire
            // (stdio: onclose comes only from the child's close event, which
            // already happened) would otherwise leave it armed to swallow the
            // NEXT genuine close after a restart.
            const saved = this._savedOnClose;
            const transport = this._transport;
            let spent = false;
            const wrapper = () => {
                if (!spent) {
                    spent = true;
                    return;
                }
                saved();
            };
            transport.onclose = wrapper;
            pendingSpentCloseGuards.set(transport, () => {
                // Restore by identity — never clobber a handler the caller
                // replaced in the meantime.
                if (transport.onclose === wrapper) {
                    transport.onclose = saved;
                }
            });
        } else {
            this._transport.onclose = this._savedOnClose;
        }
    }

    /** Detach the handlers and arm the one-shot `start()` pass-through for the `Protocol.connect()` handover. */
    release(): void {
        this.detach();
        const transport = this._transport;
        // Save the raw property value (not a bound copy) so the restore below
        // returns `transport.start` to its original identity — no wrapper or
        // bind layer accretes across repeated connects on the same transport.
        const originalStart = transport.start;
        let armed = true;
        transport.start = async function (this: unknown): Promise<void> {
            if (armed) {
                armed = false;
                transport.start = originalStart;
                return;
            }
            return originalStart.call(transport);
        };
    }
}

/**
 * Spent-close guards from failed negotiations, pending disarm by the cleanup
 * site (keyed weakly — an abandoned transport carries its guard to GC).
 */
const pendingSpentCloseGuards = new WeakMap<Transport, () => void>();

/**
 * Disarm a failed negotiation's spent-close guard once the cleanup `close()`
 * has settled. Any re-delivery of the spent mid-window close happens DURING
 * that close (HTTP transports re-fire `onclose` there; stdio transports never
 * do — their close event already fired), so after it every close is genuine
 * and must reach the caller's pre-set observer.
 */
export function disarmSpentCloseGuard(transport: Transport): void {
    const disarm = pendingSpentCloseGuards.get(transport);
    pendingSpentCloseGuards.delete(transport);
    disarm?.();
}

/** Build the probe request: `server/discover` carrying the full per-request `_meta` envelope. */
export function buildProbeRequest(
    id: string,
    protocolVersion: string,
    clientInfo: Implementation,
    capabilities: ClientCapabilities
): JSONRPCRequest {
    return {
        jsonrpc: '2.0',
        id,
        method: 'server/discover',
        params: {
            // The era codec owns the keyed-envelope shape; the probe is sent
            // for a modern version, so this is always the 2026 envelope.
            _meta: codecForVersion(protocolVersion).outboundEnvelope({
                protocolVersion,
                clientInfo,
                clientCapabilities: capabilities
            })
        }
    };
}

function normalizeReply(reply: RawProbeReply, timeoutMs: number): ProbeOutcome {
    switch (reply.kind) {
        case 'response': {
            return reply.error === undefined ? { kind: 'result', result: reply.result } : { kind: 'rpc-error', ...reply.error };
        }
        case 'send-error': {
            const error = reply.error;
            const isAuthOutcome =
                // Provenance recorded at the throw boundary: the transport
                // stamps every error escaping one of its auth seams (the
                // token() read, onUnauthorized invocations — SDK flow and
                // custom callbacks alike — the 403 step-up method, and its
                // own auth-failure constructions). Never reconstructed here
                // from error types.
                isAuthSeamEscape(error) ||
                // The published contract for foreign transports: an
                // UnauthorizedError by brand, or by name for one thrown by a
                // differently bundled SDK copy at a skewed version or an auth
                // middleware's own class.
                error instanceof UnauthorizedError ||
                (error instanceof Error && error.name === 'UnauthorizedError');
            if (isAuthOutcome) {
                // Auth-gated server: propagate unchanged.
                return { kind: 'auth-required', error: error as Error };
            }
            if (error instanceof SdkHttpError) {
                const text = (error.data as { text?: unknown } | undefined)?.text;
                return {
                    kind: 'http-error',
                    status: error.data.status,
                    body: typeof text === 'string' ? text : undefined,
                    statusText: error.data.statusText
                };
            }
            return { kind: 'network-error', error };
        }
        case 'closed': {
            // Not folded into network-error: the classifier's closed row is
            // transport-aware (stdio legacy signal vs typed error).
            return { kind: 'closed' };
        }
        case 'timeout': {
            return { kind: 'timeout', timeoutMs };
        }
    }
}

export interface NegotiationDeps {
    transport: Transport;
    clientInfo: Implementation;
    capabilities: ClientCapabilities;
    environment: ProbeEnvironment;
    /** The transport class, for the transport-aware timeout and closed verdicts (see {@linkcode ProbeTransportKind}). */
    transportKind: ProbeTransportKind;
    /** The standard request timeout for this connect (probe inherits it unless `probe.timeoutMs` overrides). */
    defaultTimeoutMs: number;
    /**
     * The probe transport is a disposable sibling (see
     * {@linkcode negotiateStdioViaSibling}): a mid-probe close is ordinary
     * legacy evidence — the probe child spent itself — never a connect-fatal
     * condition. Absent for in-place probes, where a closed connection has no
     * fallback stream and stays a typed error.
     */
    disposableProbe?: boolean;
}

export type NegotiationResult = { era: 'modern'; version: string; discover: DiscoverResult } | { era: 'legacy' };

/**
 * Run the negotiation probe state machine on a raw (not yet Protocol-connected)
 * transport. Resolves with the negotiated era; throws typed connect errors. On
 * return the probe window has been released: the transport is started,
 * handler-free, and ready for `Protocol.connect()` handover. On throw the
 * window is detached and the transport's `start` is left untouched.
 */
export async function negotiateEra(
    negotiation: Extract<ResolvedVersionNegotiation, { kind: 'auto' | 'pin' }>,
    deps: NegotiationDeps
): Promise<NegotiationResult> {
    const timeoutMs = negotiation.probe.timeoutMs ?? deps.defaultTimeoutMs;
    const maxRetries = Math.max(0, negotiation.probe.maxRetries ?? 0);
    const clientModernVersions = negotiation.kind === 'pin' ? [negotiation.version] : negotiation.modernVersions;
    const fallbackAvailable = negotiation.kind === 'auto' && negotiation.fallbackAvailable;

    const window = await ProbeWindow.open(deps.transport);

    const probe = async (): Promise<NegotiationResult> => {
        let requestedVersion = clientModernVersions[0]!;
        // The -32022 corrective continuation runs exactly once (even when the
        // mutual version equals the just-rejected one); the loop guard arms on
        // the second rejection.
        let correctiveUsed = false;
        // `maxRetries` governs timeout re-sends only — independent of (and
        // never counted against) the corrective continuation.
        let timeoutRetriesRemaining = maxRetries;
        for (;;) {
            const reply = await window.exchange(
                id => buildProbeRequest(id, requestedVersion, deps.clientInfo, deps.capabilities),
                timeoutMs
            );

            if (reply.kind === 'timeout' && timeoutRetriesRemaining > 0) {
                timeoutRetriesRemaining--;
                continue;
            }

            const outcome = normalizeReply(reply, timeoutMs);
            const verdict: ProbeVerdict = classifyProbeOutcome(outcome, {
                clientModernVersions,
                requestedVersion,
                fallbackAvailable,
                environment: deps.environment,
                transportKind: deps.transportKind
            });

            switch (verdict.kind) {
                case 'modern': {
                    return { era: 'modern', version: verdict.version, discover: verdict.discover };
                }
                case 'corrective': {
                    if (correctiveUsed) {
                        // Second rejection: loop guard.
                        throw verdict.error;
                    }
                    correctiveUsed = true;
                    requestedVersion = verdict.version;
                    continue;
                }
                case 'legacy': {
                    // A closed outcome carries its own cause — diagnostics must
                    // name the close instead of implying a server/discover
                    // verdict that never happened.
                    const closedCause = outcome.kind === 'closed' ? 'the connection closed during the server/discover probe' : undefined;
                    if (negotiation.kind === 'pin') {
                        throw new SdkError(
                            SdkErrorCode.EraNegotiationFailed,
                            closedCause === undefined
                                ? `Version negotiation failed: the server did not offer pinned protocol version ${negotiation.version} ` +
                                  `via server/discover (no fallback in pin mode)`
                                : `Version negotiation failed: ${closedCause} before the server offered ` +
                                  `pinned protocol version ${negotiation.version} (no fallback in pin mode)`
                        );
                    }
                    if (!negotiation.fallbackAvailable) {
                        // Modern-only client: the legacy initialize fallback is
                        // unavailable and must never carry a 2026-era version string.
                        throw new SdkError(
                            SdkErrorCode.EraNegotiationFailed,
                            closedCause === undefined
                                ? 'Version negotiation failed: the server gave no modern evidence and this client supports no ' +
                                  'pre-2026-07-28 protocol version to fall back to'
                                : `Version negotiation failed: ${closedCause} and this client supports no ` +
                                  'pre-2026-07-28 protocol version to fall back to'
                        );
                    }
                    if (closedCause !== undefined && deps.disposableProbe !== true) {
                        // In-place probe: the connection died with the child and
                        // there is no disposable sibling to spend — the
                        // same-stream initialize fallback is impossible, so this
                        // stays a typed connect error.
                        throw new SdkError(
                            SdkErrorCode.EraNegotiationFailed,
                            `Version negotiation failed: ${closedCause} ` +
                                "(this transport probed in place — the disposable sibling probe requires the SDK's base StdioClientTransport)"
                        );
                    }
                    return { era: 'legacy' };
                }
                case 'error': {
                    throw verdict.error;
                }
            }
        }
    };

    let result: NegotiationResult;
    try {
        result = await probe();
    } catch (error) {
        // A failed negotiation leaves the transport exactly as it found it:
        // handlers detached, original start untouched (no pass-through armed).
        window.detach();
        throw error;
    }
    window.release();
    return result;
}

/**
 * Structural read of the SDK stdio transport's retained spawn parameters —
 * `undefined` for stdio-shaped transports that do not expose them, and for
 * SUBCLASSES of the SDK transport (those probe in place, where a mid-probe
 * close stays a typed connect error). The sibling path requires the transport
 * to be exactly the base class: a subclass cannot be faithfully cloned by
 * re-invoking its constructor with the retained params alone (extra ctor
 * arguments, transformed state, side effects). Exactness is checked without
 * importing the class (the negotiation graph must stay runtime-neutral): only
 * the base class's own prototype carries the internal `_dispose` reaper the
 * sibling flow depends on — a subclass's prototype does not own it.
 */
export function readStdioServerParams(transport: Transport): Record<string, unknown> | undefined {
    const proto = Object.getPrototypeOf(transport) as object | null;
    if (proto === null || !Object.prototype.hasOwnProperty.call(proto, '_dispose')) {
        return undefined;
    }
    const params = (transport as { _serverParams?: unknown })._serverParams;
    return typeof params === 'object' && params !== null && typeof (params as { command?: unknown }).command === 'string'
        ? (params as Record<string, unknown>)
        : undefined;
}

/**
 * stdio era negotiation on a DISPOSABLE SIBLING: stdio servers built on SDKs
 * that terminate on any pre-`initialize` request exit when the probe arrives,
 * so the probe must not spend the caller's one process life. `server/discover`
 * runs on a short-lived sibling spawned from the same parameters; the caller's
 * transport starts exactly once, after the era is known. The sibling is
 * invisible infrastructure: its stderr is discarded, and it is reaped before
 * this resolves (signal escalation awaiting process exit — a helper holding
 * its pipes never blocks disposal). A caller `close()` on the session
 * transport during the probe aborts the connect with the typed negotiation
 * error, and the session transport is never started.
 */
export async function negotiateStdioViaSibling(
    negotiation: Extract<ResolvedVersionNegotiation, { kind: 'auto' | 'pin' }>,
    sessionTransport: Transport,
    params: Record<string, unknown>,
    deps: Omit<NegotiationDeps, 'transport' | 'transportKind' | 'disposableProbe'>
): Promise<NegotiationResult> {
    const SiblingTransport = sessionTransport.constructor as new (params: Record<string, unknown>) => Transport;
    const sibling = new SiblingTransport({ ...params, stderr: 'ignore' });

    // The session transport is unstarted while the sibling probes, and closing
    // an unstarted transport records nothing — so watch its close() for the
    // window's duration (raw property saved; identity restored in the finally).
    // The abort races the probe: a caller close rejects promptly instead of
    // waiting out the probe timeout.
    const originalClose = sessionTransport.close;
    let callerClosed = false;
    let signalClosed: (() => void) | undefined;
    const closedSignal = new Promise<never>((_, reject) => {
        signalClosed = () => reject(callerCloseAbortError());
    });
    sessionTransport.close = async function (): Promise<void> {
        callerClosed = true;
        signalClosed?.();
        return originalClose.call(sessionTransport);
    };

    let result: NegotiationResult;
    try {
        const negotiated = negotiateEra(negotiation, {
            ...deps,
            transport: sibling,
            transportKind: 'stdio',
            disposableProbe: true
        });
        // The abort may orphan the probe promise; its late settlement (the
        // disposed sibling's close, a timeout) must not surface anywhere.
        negotiated.catch(() => {});
        result = await Promise.race([negotiated, closedSignal]);
    } finally {
        // Dispose FIRST, with the close watch still armed: a caller close()
        // landing during the disposal window must still trip the abort below —
        // only once the sibling is reaped does the caller get its close back.
        await disposeSibling(sibling);
        sessionTransport.close = originalClose;
    }
    if (callerClosed) {
        // Checked AFTER the finally so a close during either the probe or the
        // disposal window aborts: the session transport is never started.
        throw callerCloseAbortError();
    }
    return result;
}

/** The typed abort for a caller `close()` landing while the sibling probes. */
function callerCloseAbortError(): SdkError {
    return new SdkError(
        SdkErrorCode.EraNegotiationFailed,
        'Version negotiation failed: the transport was closed during the server/discover probe'
    );
}

/** Reap a probe sibling — best-effort; a sibling that cannot be reaped must not turn a settled verdict into an error. */
async function disposeSibling(sibling: Transport): Promise<void> {
    try {
        const dispose = (sibling as { _dispose?: () => Promise<void> })._dispose;
        await (typeof dispose === 'function' ? dispose.call(sibling) : sibling.close());
    } catch {
        // ignore
    }
}
